import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Divider, 
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  CircularProgress
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { 
  Today as TodayIcon,
  Label as LabelIcon,
  AccessTime as ClockIcon,
  Comment as CommentIcon,
  Edit as Edit,
  Message as MessageIcon,
  Person as PersonIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: "bold"
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  description: {
    whiteSpace: "pre-line"
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  iconWithText: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  icon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main
  },
  tagContainer: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5)
    }
  },
  priorityHigh: {
    backgroundColor: theme.palette.error.light
  },
  priorityMedium: {
    backgroundColor: theme.palette.warning.light
  },
  priorityLow: {
    backgroundColor: theme.palette.success.light
  },
  editModeActions: {
    marginTop: theme.spacing(2)
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: "100%"
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  loader: {
    margin: theme.spacing(2)
  }
}));

const CardDetailsDialog = ({ open, onClose, card }) => {
  const classes = useStyles();
  const [editMode, setEditMode] = useState(false);
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [stages, setStages] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    tags: "",
    dueDate: null,
    stageId: ""
  });
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [cardMessages, setCardMessages] = useState([]);

  // Determinar o idioma atual para o DatePicker
  const locale = i18n.language === "pt" ? ptBR : enUS;

  useEffect(() => {
    if (card) {
      console.log("[DEBUG] Card recebido:", card);
      setFormData({
        title: card.title || "",
        description: card.description || "",
        priority: card.priority || "medium",
        tags: card.tags || "",
        dueDate: card.dueDate ? new Date(card.dueDate) : null,
        stageId: card.stageId ? card.stageId.toString() : ""
      });
      
      if (card.stage && card.stage.queueId) {
        console.log("[DEBUG] Fila do estágio do cartão:", card.stage.queueId);
        setSelectedQueue(card.stage.queueId.toString());
        loadStagesByQueue(card.stage.queueId);
      }
    }
  }, [card]);

  useEffect(() => {
    const fetchQueues = async () => {
      try {
        const { data } = await api.get("/queue");
        console.log("[DEBUG] Filas carregadas:", data);
        setQueues(data);
      } catch (err) {
        console.error("Erro ao carregar filas:", err);
      }
    };

    if (open) {
      console.log("[DEBUG] Diálogo aberto, carregando filas...");
      fetchQueues();
    }
  }, [open]);

  useEffect(() => {
    const fetchCardMessages = async () => {
      if (!card || !card.id || !open) return;
      
      try {
        setLoadingMessages(true);
        const { data } = await api.get(`/kanban/cards/${card.id}/messages`);
        setCardMessages(data);
      } catch (err) {
        console.error("Erro ao carregar mensagens do cartão:", err);
        toastError(err);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchCardMessages();
  }, [card, open]);

  const loadStagesByQueue = async (queueId) => {
    try {
      console.log("[DEBUG] Carregando estágios para a fila:", queueId);
      const { data } = await api.get(`/kanban/stages/queue/${queueId}`);
      console.log("[DEBUG] Estágios carregados:", data);
      setStages(data);
    } catch (err) {
      console.error("Erro ao carregar estágios:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQueueChange = async (e) => {
    const queueId = e.target.value;
    setSelectedQueue(queueId);
    
    // Resetar o estágio selecionado
    setFormData(prev => ({ ...prev, stageId: "" }));
    
    // Carregar os estágios da nova fila selecionada
    await loadStagesByQueue(queueId);
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, dueDate: date }));
  };

  const handleEditClick = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    // Restaurar os dados originais
    if (card) {
      setFormData({
        title: card.title || "",
        description: card.description || "",
        priority: card.priority || "medium",
        tags: card.tags || "",
        dueDate: card.dueDate ? new Date(card.dueDate) : null,
        stageId: card.stageId ? card.stageId.toString() : ""
      });
      
      if (card.stage && card.stage.queueId) {
        setSelectedQueue(card.stage.queueId.toString());
      }
    }
    setEditMode(false);
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      // Verificar se o usuário está movendo o cartão para outra fila
      let updatedData = { ...formData };
      
      await api.put(`/kanban/cards/${card.id}`, updatedData);
      
      // Se o usuário mudou o estágio (que pode ser de outra fila)
      if (updatedData.stageId && updatedData.stageId !== card.stageId?.toString()) {
        // Mover o cartão para o novo estágio
        await api.put(`/kanban/cards/${card.id}/move`, { stageId: updatedData.stageId });
      }
      
      toast.success(i18n.t("ticketCard.updated"));
  
      setEditMode(false);
      onClose(); // Fechar o diálogo após salvar
    } catch (err) {
      console.error(err);
      toastError(err.message || "Erro ao atualizar cartão");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "low":
        return i18n.t("kanban.priorities.low");
      case "high":
        return i18n.t("kanban.priorities.high");
      case "medium":
      default:
        return i18n.t("kanban.priorities.medium");
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "high":
        return classes.priorityHigh;
      case "medium":
        return classes.priorityMedium;
      case "low":
        return classes.priorityLow;
      default:
        return "";
    }
  };

  if (!card) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="card-details-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="card-details-dialog-title">
        {editMode ? (
          <TextField
            name="title"
            value={formData.title}
            onChange={handleChange}
            label={i18n.t("kanban.fields.title")}
            fullWidth
            autoFocus
            variant="outlined"
            size="small"
          />
        ) : (
          <Typography variant="h6" className={classes.title}>
            {card.title}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {/* Alerta de fila diferente */}
        {card.stage && card.stage.queueId && selectedQueue && 
         !editMode && card.stage.queueId.toString() !== selectedQueue && (
          <Alert severity="warning" className={classes.section}>
            {i18n.t("kanban.alerts.differentQueue") || "Atenção: Este cartão pertence a uma fila diferente da atual"}
          </Alert>
        )}

        {/* Descrição */}
        <div className={classes.section}>
          <Typography variant="subtitle1" gutterBottom>
            {i18n.t("kanban.fields.description")}
          </Typography>
          {editMode ? (
            <TextField
              name="description"
              value={formData.description}
              onChange={handleChange}
              label={i18n.t("kanban.fields.description")}
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
            />
          ) : (
            <Typography 
              variant="body2" 
              color="textSecondary" 
              className={classes.description}
            >
              {card.description || i18n.t("No description provided")}
            </Typography>
          )}
        </div>
        
        <Divider />
        
        {/* Fila e Estágio */}
        <div className={classes.section}>
          {editMode ? (
            <>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="queue-label">
                  {i18n.t("kanban.fields.queue") || "Fila"}
                </InputLabel>
                <Select
                  labelId="queue-label"
                  name="queueId"
                  value={selectedQueue || ""}
                  onChange={handleQueueChange}
                  label={i18n.t("kanban.fields.queue") || "Fila"}
                >
                  {queues.map(queue => (
                    <MenuItem key={queue.id} value={queue.id.toString()}>
                      {queue.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedQueue && (
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="stage-label">
                    {i18n.t("kanban.fields.stage") || "Estágio"}
                  </InputLabel>
                  <Select
                    labelId="stage-label"
                    name="stageId"
                    value={formData.stageId || ""}
                    onChange={handleChange}
                    label={i18n.t("kanban.fields.stage") || "Estágio"}
                  >
                    {stages.map(stage => (
                      <MenuItem key={stage.id} value={stage.id.toString()}>
                        {stage.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          ) : (
            card.stage && (
              <div className={classes.iconWithText}>
                <Box className={classes.icon} 
                  width={24} 
                  height={24} 
                  bgcolor={card.stage.color || "primary.main"}
                  borderRadius="50%"
                />
                <Typography variant="body2">
                  <strong>{i18n.t("kanban.fields.stage")}:</strong>{" "}
                  {card.stage.name}
                </Typography>
              </div>
            )
          )}
        </div>

        {/* Prioridade */}
        <div className={classes.section}>
          {editMode ? (
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="priority-label">
                {i18n.t("kanban.fields.priority")}
              </InputLabel>
              <Select
                labelId="priority-label"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                label={i18n.t("kanban.fields.priority")}
              >
                <MenuItem value="low">{i18n.t("kanban.priorities.low")}</MenuItem>
                <MenuItem value="medium">{i18n.t("kanban.priorities.medium")}</MenuItem>
                <MenuItem value="high">{i18n.t("kanban.priorities.high")}</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <div className={classes.iconWithText}>
              <LabelIcon className={classes.icon} />
              <div>
                <Typography variant="body2" component="span">
                  <strong>{i18n.t("kanban.fields.priority")}:</strong>{" "}
                </Typography>
                <Chip 
                  size="small" 
                  label={getPriorityLabel(card.priority)} 
                  className={getPriorityClass(card.priority)}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Data de vencimento */}
        <div className={classes.section}>
          {editMode ? (
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
              <DatePicker
                disableToolbar
                variant="inline"
                inputVariant="outlined"
                format="dd/MM/yyyy"
                margin="normal"
                label={i18n.t("kanban.fields.dueDate")}
                value={formData.dueDate}
                onChange={handleDateChange}
                fullWidth
                clearable="true"
                autoOk
              />
            </MuiPickersUtilsProvider>
          ) : (
            card.dueDate && (
              <div className={classes.iconWithText}>
                <TodayIcon className={classes.icon} />
                <Typography variant="body2">
                  <strong>{i18n.t("kanban.fields.dueDate")}:</strong>{" "}
                  {format(new Date(card.dueDate), "dd/MM/yyyy")}
                </Typography>
              </div>
            )
          )}
        </div>
        
        {/* Tags */}
        <div className={classes.section}>
          {editMode ? (
            <TextField
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              label={i18n.t("kanban.fields.tags")}
              fullWidth
              variant="outlined"
              placeholder="tag1, tag2, tag3"
              helperText={i18n.t("kanban.helperTexts.separeteCommas")}
            />
          ) : (
            card.tags && (
              <>
                <div className={classes.iconWithText}>
                  <LabelIcon className={classes.icon} />
                  <Typography variant="body2">
                    <strong>{i18n.t("kanban.fields.tags")}:</strong>
                  </Typography>
                </div>
                <div className={classes.tagContainer}>
                  {card.tags.split(",").map((tag, i) => (
                    <Chip key={i} size="small" label={tag.trim()} className={classes.chip} />
                  ))}
                </div>
              </>
            )
          )}
        </div>
        
        {/* Informações adicionais */}
        {!editMode && (
          <>
            <Divider />
            <div className={classes.section}>
              {card.stage && (
                <div className={classes.iconWithText}>
                  <Box className={classes.icon} 
                    width={24} 
                    height={24} 
                    bgcolor={card.stage.color || "primary.main"}
                    borderRadius="50%"
                  />
                  <Typography variant="body2">
                    <strong>{i18n.t("kanban.fields.stage")}:</strong>{" "}
                    {card.stage.name}
                  </Typography>
                </div>
              )}
              
              {card.user && (
                <div className={classes.iconWithText}>
                  <Typography variant="body2">
                    <strong>{i18n.t("kanban.fields.assignedUser")}:</strong>{" "}
                    {card.user.name}
                  </Typography>
                </div>
              )}
              
              {card.ticket && (
                <div className={classes.iconWithText}>
                  <CommentIcon className={classes.icon} />
                  <Typography variant="body2">
                    <strong>Ticket:</strong> #{card.ticket.id}
                  </Typography>
                </div>
              )}
              
              {card.createdAt && (
                <div className={classes.iconWithText}>
                  <ClockIcon className={classes.icon} />
                  <Typography variant="body2">
                    <strong>{i18n.t("kanban.fields.createdAt")}:</strong>{" "}
                    {format(new Date(card.createdAt), "dd/MM/yyyy HH:mm")}
                  </Typography>
                </div>
              )}
              
              {card.updatedAt && card.updatedAt !== card.createdAt && (
                <div className={classes.iconWithText}>
                  <ClockIcon className={classes.icon} />
                  <Typography variant="body2">
                    <strong>{i18n.t("kanban.fields.updatedAt")}:</strong>{" "}
                    {format(new Date(card.updatedAt), "dd/MM/yyyy HH:mm")}
                  </Typography>
                </div>
              )}
            </div>
          </>
        )}

        {/* Mensagens do cartão */}
        <Divider />
        <div className={classes.section}>
          <Typography variant="subtitle1" gutterBottom>
            {i18n.t("kanban.fields.messages") || "Mensagens do contexto"}
          </Typography>
          {loadingMessages ? (
            <CircularProgress size={24} className={classes.loader} />
          ) : (
            <List>
              {cardMessages.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("kanban.noMessages") || "Nenhuma mensagem vinculada ao contexto do cartão"}
                </Typography>
              ) : (
                cardMessages.map(cardMessage => {
                  // Acessar a mensagem a partir do objeto cardMessage (que contém a relação)
                  const message = cardMessage.message || {};
                  
                  // Determinar quem enviou a mensagem (contato ou usuário)
                  const isFromContact = Boolean(message.contact);
                  const senderName = isFromContact 
                    ? (message.contact?.name || "Contato") 
                    : (message.user?.name || "Atendente");
                  
                  return (
                    <ListItem key={cardMessage.id} divider>
                      <ListItemAvatar>
                        <Avatar className={classes.avatar}>
                          {isFromContact ? "C" : "A"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{senderName}</strong> -{" "}
                            {format(new Date(message.createdAt || new Date()), "dd/MM/yyyy HH:mm")}
                          </Typography>
                        }
                        secondary={
                          <Typography 
                            variant="body2" 
                            color="textSecondary"
                            className={classes.description}
                          >
                            {message.body || ""}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {editMode ? (
          <>
            <Button onClick={handleCancelEdit} color="default">
              {i18n.t("kanban.buttons.cancel")}
            </Button>
            <Button 
              onClick={handleSaveChanges} 
              color="primary"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                i18n.t("kanban.buttons.save")
              )}
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={handleEditClick} 
              color="primary"
              startIcon={<Edit />}
            >
              {i18n.t("kanban.actions.editCard")}
            </Button>
            <Button onClick={onClose} color="default">
              {i18n.t("kanban.buttons.close")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CardDetailsDialog;